import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Info, Settings } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { useCategorySelection } from '../../shared/hooks/useCategorySelection';
import FooterNavigation from '../../shared/components/Navigation';
import Pagination from '../../shared/components/Pagination';
import CategoryDisplay from '../../layouts/centered/components/CategoryDisplay';
import { Category } from '../../layouts/centered/components/CategoryCard';
import { CategorySelectionSkeleton } from '../../layouts/centered/components/CategoryCardSkeleton';
import { CategoryModal } from '../../layouts/centered/components/CategoryModal';


// New component for no categories message
const NoCategoriesMessage = ({ colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Default colors if not provided
  //@ts-expect-error
  const titleText = colors.titleText || '#DDB889';
  //@ts-expect-error
  const cardBackground = colors.cardBackground || 'rgba(0, 0, 0, 0.95)';
  //@ts-expect-error
  const cardBorder = colors.cardBorder || '#DDB88919';
  //@ts-expect-error
  const cardShadow = colors.cardShadow || 'rgba(221,184,137,0.07)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-flex lp-flex-col lp-items-center lp-justify-center lp-min-h-[400px] lp-text-center lp-p-8"
    >
      <div
        className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-8 lp-max-w-lg
          lp-transition-all lp-duration-700"
        style={{
          backgroundColor: cardBackground,
          borderColor: `${cardBorder}`,
          boxShadow: `0 0 15px ${cardShadow}`
        }}
      >
        <CheckCircle2
          className="lp-w-12 lp-h-12 lp-mx-auto lp-mb-4"
          style={{ color: titleText }}
        />
        <h3
          className="lp-text-xl lp-font-medium lp-mb-3"
          style={{ color: titleText }}
        >
          {t('categories.noCategoriesAvailable')} {/* CHANGED: "No Categories Available" */}
        </h3>
        <p className="lp-text-gray-300 lp-mb-4">
          {t('categories.noCategoriesDescription')} {/* CHANGED: "There are currently no categories available..." */}
        </p>
        <div
          className="lp-flex lp-items-center lp-justify-center lp-gap-2 lp-text-sm"
          style={{ color: `${titleText}CC` }}
        >
          <Info size={16} />
          <span>{t('categories.checkBackLater')}</span> {/* CHANGED: "Please wait until categories become available" */}
        </div>
      </div>
    </motion.div>
  );
};

interface CategorySelectionProps {
  selectedCategories: Category[];
  widgetConfiguration: any;
  setSelectedCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: any;
}

const CategorySelection: React.FC<CategorySelectionProps> = ({
  selectedCategories,
  setSelectedCategories,
  widgetConfiguration,
  onNext,
  onPrev,
  nodeData = null,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Set default nodeData if none is provided
  const defaultNodeData = {
    label: t('booking.steps.categories'), // CHANGED: "Categories"
    settings: {
      displayMode: "cards",
      showImages: true,
      showDescriptions: true,
      displayStyle: "card",
      itemsPerRow: 2,
      numberRows: 2,
      colors: {
        cardBackground: "#000000F2",
        cardBorder: "#DDB88919",
        cardBorderSelected: "#DDB889",
        cardShadow: "#DDB88919",
        cardShadowSelected: "#DDB88933",
        titleText: "#DDB889",
        subtitleText: "#9CA3AF",
        infoText: "#DDB889",
        iconColor: "#DDB889",
        selectionBackground: "#DDB889",
        selectionIcon: "#000000",
        badgeBackground: "#000000F2",
        badgeBorder: "#DDB88919",
        buttonBackground: "#DDB889",
        buttonText: "#000000",
        placeholderGradientFrom: "#DDB88933",
        placeholderGradientTo: "#DDB8890D",
        placeholderIconBackground: "#DDB88933"
      }
    }
  };

  // Use nodeData if provided, otherwise use default
  const effectiveNodeData = nodeData || defaultNodeData;

  // Calculate items per page based on nodeData settings
  // itemsPerRow × numberRows determines total items per page
  const itemsPerPage = (effectiveNodeData?.settings?.itemsPerRow || 2) *
                      (effectiveNodeData?.settings?.numberRows || 2);

  const {
    expandedDescriptionId,
    queryResult,
    toggleCategory,
    handlePageChange,
    setExpandedDescriptionId,
    shouldShowReadMore,
  } = useCategorySelection({
    selectedCategories,
    //@ts-expect-error
    setSelectedCategories,
    itemsPerPage
  });

  // Check if categories exist
  const hasCategories = queryResult.data?.serviceCategories?.edges.length > 0;

  return (
    <>
      <motion.div
        className="lp-flex lp-flex-col lp-relative lp-overflow-hidden lp-rounded-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
         <Pagination
            handlePageChange={(direction: string) => handlePageChange(direction === 'next' ? 'next' : 'previous')}
            pageInfo={queryResult.data?.serviceCategories.pageInfo || { hasNextPage: false, hasPreviousPage: false }}
            title={t('categories.selectCategory')} // CHANGED: 'Select category'
            Icon={Settings}
            showNavigation={true}
          />
        <div className="lp-w-full lp-max-w-5xl lp-relative lp-z-10">
          <AnimatePresence mode="wait">
            {queryResult.fetching && !queryResult.data ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <CategorySelectionSkeleton />
              </motion.div>
            ) : hasCategories ? (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <CategoryDisplay
                  categories={queryResult.data?.serviceCategories.edges.map(({ node }: { node: Category }) => node) || []}
                  selectedCategories={selectedCategories}
                  onToggle={toggleCategory}
                  shouldShowReadMore={shouldShowReadMore}
                  onReadMore={(id: string) => setExpandedDescriptionId(id)}
                  nodeData={effectiveNodeData}
                />
              </motion.div>
            ) : (
              <motion.div
                key="no-categories"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <NoCategoriesMessage colors={effectiveNodeData.settings.colors} />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="lp-mt-8" />
        </div>

        <AnimatePresence>
          {expandedDescriptionId && queryResult.data && (
            <CategoryModal
              category={queryResult.data.serviceCategories.edges.find(
                ({ node }: { node: Category }) => node.id === expandedDescriptionId
              )?.node}
              onClose={() => setExpandedDescriptionId(null)}
            />
          )}
        </AnimatePresence>
      </motion.div>
      {hasCategories ? (
        <FooterNavigation
          onPrev={widgetConfiguration.isLocationsActivated ? onPrev : null}
          onNext={onNext}
          disabled={selectedCategories.length === 0}
        />
      ) : null}
    </>
  );
};

export default CategorySelection;
